import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw } from 'lucide-react';

/**
 * SecondaryRail - Row 2 of the rail navigation
 * Displays filter chips with instant apply functionality
 * 
 * @param {Array} filters - Array of filter objects
 * @param {Function} onFilterChange - Callback when filter changes
 * @param {Function} onReset - Callback to reset all filters
 * @param {boolean} hasActiveFilters - Whether any non-default filters are active
 */
const SecondaryRail = ({ 
  filters = [], 
  onFilterChange,
  onReset,
  hasActiveFilters = false
}) => {
  const scrollContainerRef = useRef(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  // Check scroll position for gradient indicators
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftGradient(scrollLeft > 0);
    setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [filters]);

  const getChipColor = (filter) => {
    if (filter.hasNonDefault) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    return 'bg-white/10 text-white/70 border-white/20';
  };

  const getChipLabel = (filter) => {
    // Handle special formatting for different filter types
    switch (filter.type) {
      case 'date':
        return filter.label || 'Select Date';
      case 'country':
        if (filter.multiple && filter.value?.length > 1) {
          return `${filter.label} +${filter.value.length - 1}`;
        }
        return filter.label || 'All Countries';
      case 'league':
        return filter.label || 'All Leagues';
      default:
        return filter.label || filter.id;
    }
  };

  return (
    <div className="relative bg-black/5 backdrop-blur-sm border-b border-white/10">
      {/* Left gradient indicator */}
      {showLeftGradient && (
        <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-black/40 to-transparent z-10 pointer-events-none" />
      )}
      
      {/* Right gradient indicator */}
      {showRightGradient && (
        <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-black/40 to-transparent z-10 pointer-events-none" />
      )}
      
      {/* Scrollable chip container - More compact */}
      <div 
        ref={scrollContainerRef}
        className="flex gap-1.5 px-3 py-2 overflow-x-auto scrollbar-hide scroll-smooth"
        style={{ 
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {filters.map((filter) => {
          const Icon = filter.icon;
          
          return (
            <motion.button
              key={filter.id}
              onClick={() => onFilterChange(filter.id, filter)}
              className={`
                flex items-center gap-1.5 px-2.5 py-1 rounded-full whitespace-nowrap
                transition-all duration-200 min-h-[28px] text-xs font-medium
                border ${getChipColor(filter)}
                hover:bg-white/15 active:scale-98
              `}
              whileTap={{ scale: 0.98 }}
              aria-label={`Filter by ${filter.id}`}
            >
              {Icon && <Icon size={14} />}
              
              {/* Country flag */}
              {filter.type === 'country' && filter.flag && (
                <span className="text-base">{filter.flag}</span>
              )}
              
              <span>{getChipLabel(filter)}</span>
              
              {/* Dropdown indicator */}
              {filter.hasDropdown && (
                <svg 
                  className="w-2.5 h-2.5 ml-0.5 opacity-50" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 9l-7 7-7-7" 
                  />
                </svg>
              )}
              
              {/* Active indicator */}
              {filter.hasNonDefault && (
                <span className="flex h-1.5 w-1.5 ml-1">
                  <span className="animate-ping absolute inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
              )}
            </motion.button>
          );
        })}
        
        {/* Reset button - only shows when filters are active */}
        <AnimatePresence>
          {hasActiveFilters && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={onReset}
              className="
                flex items-center gap-1.5 px-2.5 py-1 rounded-full whitespace-nowrap
                transition-all duration-200 min-h-[28px] text-xs font-medium
                bg-red-500/20 text-red-400 border border-red-500/30
                hover:bg-red-500/30 active:scale-98
              "
              whileTap={{ scale: 0.98 }}
              aria-label="Reset all filters"
            >
              <RotateCcw size={12} />
              <span>Reset</span>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SecondaryRail;